import { PSTFieldNames } from "@common/data-models/fields/pst-field-names";
import { ObjectTypes } from "@common/enums/object-types";
import { GroupValueHelpers, GroupKeyComparatorHelpers, RecordComparatorHelpers, ResultGroup, ResultGroupingDefinition, ResultGroup2, ResultGrouping2Definition } from "@common/data-models/search-results/result-group";
import { AssignmentGroupValueHelpers } from "@playbook/data-models/playbook-search-results/playbook-result-group";
import { SearchResults, TreeLikeNode } from "@common/data-models/search-results/search-results";
import { Record } from "@common/data-models/records/record";
import { TopicRecord } from "@playbook/data-models/records/topic-record";
import { AssignmentRecord } from "@playbook/data-models/records/assignment-record";
import { TopicTemplateRecord } from "@playbook/data-models/records/topic-template-record";
import { DataStore } from '@common/data-models/data-store/data-store';
import { IPlaybookService } from "@suite/playbook/interfaces/i-playbook-service";
import { FieldDef } from "@suite/common/data-models/service/field-def";
import { TileColumn } from "@suite/common/data-models/misc/tile-column";
import { GroupKey } from "@suite/common/data-models/search-results/group-key";
import { MultiValue } from "@suite/common/data-models/complex-types/multi-value";
import { TileDay } from "@suite/common/data-models/misc/tile-day";
import { DateTime } from "@suite/common/data-models/complex-types/date-time";
import { NewlyCreatedRecords } from "@suite/common/data-models/newly-created-records/newly-created-records";
import { StaffIDListSerializer, ChecklistSerializer, ViewStateOrderSerializer } from "@suite/common/data-models/serializers/serializers";
import { ViewStateOrder } from '@suite/common/data-models/complex-types/view-state-order';
import { map, takeUntil } from "rxjs/operators";
import moment from 'moment-timezone';
import { combineLatest } from "rxjs";

export class PlaybookSearchResults extends SearchResults {
    public topics: Array<TopicRecord> = [];
    public assignments: Array<AssignmentRecord> = [];
    public templates: Array<TopicTemplateRecord> = [];
    public static playbookService: IPlaybookService = null;

    constructor() {
        super();
        PlaybookSearchResults.newlyAddedItems = new NewlyCreatedRecords();
    }

    public getAllResults(): Array<Record> {
        let ret: Array<Record> = [];
        for (let topic of this.topics) {
            ret.push(topic);
        }
        for (let assignment of this.assignments) {
            ret.push(assignment);
        }
        for (let template of this.templates) {
            ret.push(template);
        }
        return ret;
    }

    public topicGroups: { [id: string]: ResultGroup<TopicRecord> } = {};
    public assignmentSearchGroup: ResultGroup<AssignmentRecord> = null;
    public topicSearchGroup: ResultGroup<TopicRecord> = null;
    public assignmentGroups: { [id: string]: ResultGroup<AssignmentRecord> } = {};

    public static topicGroupDefs: { [id: string]: ResultGroupingDefinition } = {};
    private get topicGroupFields(): Array<string> {
        return PlaybookSearchResults.playbookService.groupByTopicKeys;
    }

    public static assignmentSearchGroupDef: { [id: string]: ResultGroupingDefinition } = {};

    public static assignmentGroupDefs: { [id: string]: ResultGroupingDefinition } = {};
    private get assignmentGroupFields(): Array<string> {
        return PlaybookSearchResults.playbookService.groupByAssignmentKeys;
    }

    public addTopic(topic: TopicRecord) {
        this.topics.push(topic);
        this.byGuid[topic.guid] = topic;
    }

    public addTemplate(template: TopicTemplateRecord) {
        this.templates.push(template);
        this.byGuid[template.guid] = template;
    }

    public insertAfterTopic(topic: TopicRecord, topicToInsert: TopicRecord, isNew: boolean) {
        if (!isNew) {
            this.topics.push(topicToInsert);
            this.byGuid[topicToInsert.guid] = topicToInsert;
            if (this.objectType == ObjectTypes.TOPIC) {
                this.topicGroups[PlaybookSearchResults.groupByField].insertAfterEntry(PlaybookSearchResults.topicGroupDefs[PlaybookSearchResults.groupByField].valueConvertor(topic), topic, topicToInsert);
            } else if (!this.objectType) {
                this.topicSearchGroup.insertAfterEntry(PlaybookSearchResults.topicGroupDefs["SingleGroup"].valueConvertor(topic), topic, topicToInsert)
            }
        } else {
            for (let i = 0; i < PlaybookSearchResults.newlyAddedItems.items.length; i++) {
                if (PlaybookSearchResults.newlyAddedItems.items[i].guid == topic.guid) {
                    PlaybookSearchResults.newlyAddedItems.addRecord(topicToInsert, i);
                    break;
                }
            }
            if (!PlaybookSearchResults.newlyAddedItems.items.length) {
                PlaybookSearchResults.newlyAddedItems.addRecord(topicToInsert);
            }
        }
        this.buildTree(PlaybookSearchResults.groupByField)
    }

    public insertAfterAssignment(insertAfter: AssignmentRecord, assignmentToInsert: AssignmentRecord) {
        this.assignments.push(assignmentToInsert);
        this.byGuid[assignmentToInsert.guid] = assignmentToInsert;
        if (this.objectType == ObjectTypes.ASSIGNMENT) {
            let groups = this.getGroups();
            for (let field of groups) {
                if (this.assignmentGroups[field]) {
                    this.assignmentGroups[field].insertAfterEntry(PlaybookSearchResults.assignmentGroupDefs[field].valueConvertor(insertAfter), insertAfter, assignmentToInsert);
                    this.buildTree(field);
                }
            }
        } else if (this.assignmentSearchGroup) {
            this.assignmentSearchGroup.addToGroups(PlaybookSearchResults.assignmentSearchGroupDef[PlaybookSearchResults.sortByField].valueConvertor(assignmentToInsert), assignmentToInsert);
        }
    }

    public addAssignment(assignment: AssignmentRecord) {
        this.assignments.push(assignment);
        this.byGuid[assignment.guid] = assignment;
    }

    public remove(rec: Record) {
        super.remove(rec);

        if (rec.type == ObjectTypes.TOPIC) {
            for (let i = 0; i < this.topics.length; i++) {
                if (this.topics[i].guid == rec.guid) {
                    this.topics.splice(i, 1);
                    break;
                }
            }
            for (let field in PlaybookSearchResults.topicGroupDefs) {
                if (this.topicGroups[field]) this.topicGroups[field].remove(PlaybookSearchResults.topicGroupDefs[field].valueConvertor(rec), <TopicRecord>rec);
            }

            if (this.topicSearchGroup) {
                this.topicSearchGroup.remove(PlaybookSearchResults.topicGroupDefs["SingleGroup"].valueConvertor(rec), <TopicRecord>rec)
            }
        } else if (rec.type == ObjectTypes.ASSIGNMENT) {
            for (let i = 0; i < this.assignments.length; i++) {
                if (this.assignments[i].guid == rec.guid) {
                    this.assignments.splice(i, 1);
                    break;
                }
            }
            for (let field in PlaybookSearchResults.assignmentGroupDefs) {
                if (this.assignmentGroups[field]) {
                    this.assignmentGroups[field].remove(PlaybookSearchResults.assignmentGroupDefs[field].valueConvertor(rec), <AssignmentRecord>rec);
                }
            }
            if (this.assignmentSearchGroup) {
                this.assignmentSearchGroup.remove(PlaybookSearchResults.assignmentSearchGroupDef["Title"].valueConvertor(rec), <AssignmentRecord>rec);
            }
        }
        this.buildTree(PlaybookSearchResults.groupByField);
    }

    public sortGroups(sortBy: any, sortOrder: string, groupSortOrder: string) {
        if (this.objectType == ObjectTypes.TOPIC) {
            this.topicGroups[PlaybookSearchResults.groupByField].sort(PlaybookSearchResults.topicGroupDefs[PlaybookSearchResults.groupByField].groupKeyComparator(groupSortOrder), PlaybookSearchResults.topicGroupDefs[sortBy].recordComparator(sortOrder));
        } else if (this.objectType == ObjectTypes.ASSIGNMENT) {
            this.assignmentGroups[PlaybookSearchResults.groupByField].sort(PlaybookSearchResults.assignmentGroupDefs[PlaybookSearchResults.groupByField].groupKeyComparator(groupSortOrder), PlaybookSearchResults.assignmentGroupDefs[sortBy].recordComparator(sortOrder));
        } else {
            this.topicSearchGroup.sort(PlaybookSearchResults.topicGroupDefs["SingleGroup"].groupKeyComparator(groupSortOrder), PlaybookSearchResults.topicGroupDefs[sortBy].recordComparator(sortOrder))
            this.assignmentSearchGroup.sort(PlaybookSearchResults.assignmentGroupDefs["Title"].groupKeyComparator(groupSortOrder), PlaybookSearchResults.assignmentGroupDefs[sortBy].recordComparator(sortOrder));
        }
        this.buildTree(PlaybookSearchResults.groupByField);
    }

    public dispose() {
        super.dispose();

        this.objectType = null;
        this.topics = [];
        this.assignments = [];
        this.users = [];
        this.topicGroups = {};
        this.assignmentSearchGroup = null;
        this.topicSearchGroup = null;
        this.assignmentGroups = {};
    }

    public getGroups(): Array<string> {
        switch (this.objectType) {
            case ObjectTypes.TOPIC:
                return this.topicGroupFields;
            case ObjectTypes.ASSIGNMENT:
                return this.assignmentGroupFields;
            default:
                return ["Type"];
        }
    }


    public getGroupCount(key: string): number {
        switch (this.objectType) {
            case ObjectTypes.TOPIC:
                return this.topicGroups[key]?.keys?.length || 0;
            case ObjectTypes.ASSIGNMENT:
                return this.assignmentGroups[key]?.keys?.length || 0;
            default:
                return 0;
        }
    }

    public async rebuildGroups(groupBy: string, sortBy: any, sortOrder: string, groupSortOrder: string) {
        if (this.objectType == ObjectTypes.TOPIC) {
            // Clear all groups
            if (!this.topicGroups[groupBy]) this.topicGroups[groupBy] = new ResultGroup<TopicRecord>();
            this.topicGroups[groupBy].clear();

            if (!this.topicGroups[PlaybookSearchResults.groupByField]) {
                PlaybookSearchResults.groupByField = groupBy;
            }
            // Build active group
            for (let topic of this.topics) {
                if (this.topicGroups[groupBy]) {
                    this.topicGroups[groupBy]?.addToGroups(PlaybookSearchResults?.topicGroupDefs?.[groupBy]?.valueConvertor(topic), topic);
                }
            }
            this.topicGroups[groupBy].sort(PlaybookSearchResults.topicGroupDefs?.[groupBy]?.groupKeyComparator(groupSortOrder), PlaybookSearchResults.topicGroupDefs?.[sortBy]?.recordComparator(sortOrder));
            this.topicGroups[groupBy].isBuilt = true;

            // if (!this.topicGroups[groupBy]) {
            //     return;
            // }
            // if (this.topicGroups[groupBy].isBuilt) {
            //     return;
            // }
            // if (Record.globalTablesService.isDateTimeFieldType(groupBy) || groupBy == PSTFieldNames.PSTModTime) {
            //     groupSortOrder = 'desc';
            //     sortOrder = 'desc'
            // } else {
            //     groupSortOrder = 'asc';
            //     sortOrder = 'asc'
            // }
            // this.topicGroups[groupBy].sort(PlaybookSearchResults.topicGroupDefs[groupBy].groupKeyComparator(groupSortOrder), PlaybookSearchResults.topicGroupDefs[sortBy].recordComparator(sortOrder));
            // this.topicGroups[groupBy].isBuilt = true;
            this.buildTree(groupBy);
        } else if (this.objectType == ObjectTypes.ASSIGNMENT) {
            // Clear all groups
            if (!this.assignmentGroups[groupBy]) this.assignmentGroups[groupBy] = new ResultGroup<AssignmentRecord>();
            this.assignmentGroups[groupBy].clear();

            if (!this.assignmentGroups[PlaybookSearchResults.groupByField]) {
                PlaybookSearchResults.groupByField = groupBy;
            }
            // Build active group
            for (let assignment of this.assignments) {
                if (this.assignmentGroups[groupBy]) {
                    this.assignmentGroups[groupBy].addToGroups(PlaybookSearchResults.assignmentGroupDefs?.[groupBy]?.valueConvertor(assignment), assignment);
                }
            }
            this.assignmentGroups[groupBy].sort(PlaybookSearchResults.assignmentGroupDefs?.[groupBy]?.groupKeyComparator(groupSortOrder), PlaybookSearchResults.assignmentGroupDefs?.[sortBy]?.recordComparator(sortOrder));
            this.assignmentGroups[groupBy].isBuilt = true;

            // Build the rest of the groups
            // if (!this.assignmentGroups[groupBy]) {
            //     return;
            // }
            // if (this.assignmentGroups[groupBy].isBuilt) {
            //     return;
            // }
            // if (Record.globalTablesService.isDateTimeFieldType(groupBy) || groupBy == PSTFieldNames.PSTModTime) {
            //     groupSortOrder = 'desc';
            //     sortOrder = 'desc'
            // } else {
            //     groupSortOrder = 'asc';
            //     sortOrder = 'asc'
            // }
            // this.assignmentGroups[groupBy].sort(PlaybookSearchResults.assignmentGroupDefs[groupBy].groupKeyComparator(groupSortOrder), PlaybookSearchResults.assignmentGroupDefs[sortBy].recordComparator(sortOrder));
            // this.assignmentGroups[groupBy].isBuilt = true;
            this.buildTree(groupBy);
        } else {
            this.assignmentSearchGroup = new ResultGroup<AssignmentRecord>();
            for (let assignment of this.assignments) {
                if (this.assignmentSearchGroup) {
                    this.assignmentSearchGroup.addToGroups(PlaybookSearchResults.assignmentGroupDefs?.["Title"]?.valueConvertor(assignment), assignment);
                }
            }

            this.topicSearchGroup = new ResultGroup<TopicRecord>();
            for (let topic of this.topics) {
                if (this.topicSearchGroup) {
                    this.topicSearchGroup.addToGroups(PlaybookSearchResults.topicGroupDefs?.["SingleGroup"]?.valueConvertor(topic), topic);
                }
            }

            this.topicSearchGroup.sort(PlaybookSearchResults.topicGroupDefs?.["SingleGroup"]?.groupKeyComparator(groupSortOrder), PlaybookSearchResults.topicGroupDefs?.[sortBy]?.recordComparator(sortOrder))
            this.assignmentSearchGroup.sort(PlaybookSearchResults.assignmentGroupDefs?.["Title"]?.groupKeyComparator(groupSortOrder), PlaybookSearchResults.assignmentGroupDefs?.[sortBy]?.recordComparator(sortOrder));
            this.topicSearchGroup.isBuilt = true;
            this.assignmentSearchGroup.isBuilt = true;

            this.buildTree(groupBy);
        }
    }

    public async changeTopicOrder(sourceGuid: string, targetGuid: string, targetGroup: any, referalId: string, viewStateGuid: string) {
        let sourceRecord: TopicRecord = DataStore.byGuid<TopicRecord>(sourceGuid, referalId);

        let lastValidOrder: number = 1000;
        let currentOrder: number = 0;
        let targetRecord: TopicRecord = null;
        let prevRecord: TopicRecord = null;

        let groupKey: string = "";
        if (targetGroup instanceof TileDay) {
            groupKey = targetGroup.value;
        } else if (targetGroup instanceof TileColumn) {
            groupKey = targetGroup.name;
        } else if (targetGroup instanceof GroupKey) {
            groupKey = targetGroup.value;
        } else {
            groupKey = targetGroup;
        }

        let needsReorder: boolean = false;

        if (!targetGuid && this.topicGroups[PlaybookSearchResults.groupByField].entries[groupKey].length > 0) {
            targetGuid = this.topicGroups[PlaybookSearchResults.groupByField].entries[groupKey][0].guid;
        }

        for (let currentIdx = 0; currentIdx < this.topicGroups[PlaybookSearchResults.groupByField].entries[groupKey].length; currentIdx++) {
            let currentRecord: TopicRecord = this.topicGroups[PlaybookSearchResults.groupByField].entries[groupKey][currentIdx];
            let currentViewStateOrder: ViewStateOrder = currentRecord.get(PSTFieldNames.PSTTopicOrder, new ViewStateOrder());
            let currentRecordOrder: number = currentViewStateOrder.get(viewStateGuid);

            needsReorder = false;
            if (!currentRecordOrder || currentRecordOrder == 0 || currentRecordOrder != lastValidOrder) {
                needsReorder = true;
                currentOrder = lastValidOrder;
            }

            if (needsReorder) {
                currentViewStateOrder.set(viewStateGuid, currentOrder);
                let data: { [id: string]: any } = {};
                data[PSTFieldNames.PSTTopicOrder] = ViewStateOrderSerializer.Instance(SearchResults.appService.globalTablesService).serialize(currentViewStateOrder);
                data[PSTFieldNames.Type] = currentRecord.type;
                await currentRecord.saveChanges(SearchResults.appService, null, null, data);
                lastValidOrder = currentOrder;
            }

            lastValidOrder += 1000;

            if (currentRecord.guid == targetGuid) {
                targetRecord = currentRecord;
            }
        }

        if (targetRecord) {
            let targetOrder: number = (<ViewStateOrder>targetRecord.get(PSTFieldNames.PSTTopicOrder)).get(viewStateGuid);
            currentOrder = targetOrder - 500;
            let currentViewStateOrder: ViewStateOrder = sourceRecord.get(PSTFieldNames.PSTTopicOrder, new ViewStateOrder());
            currentViewStateOrder.set(viewStateGuid, currentOrder);

            let data: { [id: string]: any } = {};
            data[PSTFieldNames.PSTTopicOrder] = ViewStateOrderSerializer.Instance(SearchResults.appService.globalTablesService).serialize(currentViewStateOrder);
            data[PSTFieldNames.Type] = sourceRecord.type;
            await sourceRecord.saveChanges(SearchResults.appService, null, null, data);
        }
    }

    public async changeGroup(rec: Record, groupByField: FieldDef, sourceGroup: GroupKey | TileDay | string, targetGroup: GroupKey | TileDay | string) {
        if (rec instanceof TopicRecord || rec instanceof AssignmentRecord) {
            let sourceGroupID: string = "";
            if (sourceGroup instanceof TileColumn) {
                sourceGroupID = sourceGroup.name;
            } else if (sourceGroup instanceof GroupKey) {
                sourceGroupID = sourceGroup.value;
            } else {
                sourceGroupID = sourceGroup;
            }
            let targetGroupID: string = "";
            if (targetGroup instanceof TileColumn) {
                targetGroupID = targetGroup.name;
            } else if (targetGroup instanceof GroupKey) {
                targetGroupID = targetGroup.value;
            } else {
                targetGroupID = targetGroup;
            }
            switch (groupByField.Validation.toLowerCase()) {
                case 'text':
                case 'combobox':
                case 'checkbox':
                case 'tree': {
                    if (this.checkNoValue(targetGroupID)) {
                        rec.set(groupByField.ID, '')
                    } else {
                        rec.set(groupByField.ID, targetGroupID)
                    }
                    let data: { [id: string]: any } = {};
                    data[groupByField.ID] = rec.get(groupByField.ID);
                    data[PSTFieldNames.Type] = rec.type;
                    await rec.saveChanges(SearchResults.appService, null, null, data);
                    break;
                }
                case 'checklistedit':
                case 'staffidlist':
                case 'checklist': {
                    let prop = rec.getAs<MultiValue<string>>(groupByField.ID)
                    prop.delete(sourceGroupID);
                    prop.set(targetGroupID);
                    rec.set(groupByField.ID, prop);
                    if (this.checkNoValue(targetGroupID)) {
                        prop.clear()
                        rec.set(groupByField.ID, prop)
                    }
                    let data: { [id: string]: any } = {};
                    if (SearchResults.appService.globalTablesService.isStaffIDListFieldType(groupByField.ID)) {
                        data[groupByField.ID] = StaffIDListSerializer.Instance(SearchResults.appService.globalTablesService).serialize(prop, groupByField.ID);
                    } else {
                        data[groupByField.ID] = ChecklistSerializer.Instance.serialize(prop);
                    }
                    data[PSTFieldNames.Type] = rec.type;
                    await rec.saveChanges(SearchResults.appService, null, null, data);
                    break;
                }
                case 'date':
                case 'time': {
                    let newDate: DateTime = new DateTime();
                    if (targetGroup instanceof TileDay) {
                        newDate.set(moment([
                            targetGroup.year,
                            targetGroup.month,
                            targetGroup.day,
                            rec.fileDate.moment.hour(),
                            rec.fileDate.moment.minute()
                        ]).toDate());
                        if (this.checkNoValue(targetGroup.name)) {
                            rec.set(groupByField.ID, null)
                        }
                    } else {
                        newDate = new DateTime(targetGroupID);
                        if (this.checkNoValue(targetGroupID)) {
                            rec.set(groupByField.ID, null)
                        }
                    }

                    rec.fileDate = newDate;
                    let data: { [id: string]: any } = {};
                    data[groupByField.ID] = rec.fileDate.get();
                    data[PSTFieldNames.Type] = rec.type;
                    rec.saveChanges(SearchResults.appService, null, null, data);
                    break;
                }
                default:
            }
        }
    }

    public async createTopicInGroup(rec: Record, groupByField: FieldDef, targetGroup: GroupKey | TileDay) {
        if (rec instanceof TopicRecord || rec instanceof AssignmentRecord) {
            switch (groupByField.Validation.toLowerCase()) {
                case 'text':
                case 'combobox':
                case 'checkbox':
                case 'tree': {
                    if (targetGroup instanceof GroupKey) {
                        if (this.checkNoValue(targetGroup.value)) {
                            rec.set(groupByField.ID, '')
                        } else {
                            rec.set(groupByField.ID, targetGroup.value)
                        }
                        let data: { [id: string]: any } = {};
                        data[groupByField.ID] = rec.get(groupByField.ID);
                        data[PSTFieldNames.Type] = rec.type;
                    }
                    break;
                }
                case 'checklistedit':
                case 'staffidlist':
                case 'checklist': {
                    if (targetGroup instanceof GroupKey) {
                        let prop = new MultiValue<string>();
                        rec.set(groupByField.ID, prop);
                        prop.set(targetGroup.value);
                        rec.set(groupByField.ID, prop);
                        if (this.checkNoValue(targetGroup.value)) {
                            prop.clear()
                            rec.set(groupByField.ID, prop)
                        }
                    }
                    break;
                }
                case 'date':
                case 'time': {
                    if ((targetGroup instanceof TileDay || targetGroup instanceof GroupKey)) {
                        let newDate: DateTime = new DateTime();
                        if (targetGroup instanceof TileDay) {
                            newDate.set(moment([
                                targetGroup.year,
                                targetGroup.month,
                                targetGroup.day,
                                rec.fileDate.moment.hour(),
                                rec.fileDate.moment.minute()
                            ]).toDate());
                            if (this.checkNoValue(targetGroup.name)) {
                                rec.set(groupByField.ID, null)
                            }
                        } else {
                            newDate = new DateTime(targetGroup.value);
                            if (this.checkNoValue(targetGroup.value)) {
                                rec.set(groupByField.ID, null)
                            }
                        }

                        rec.fileDate = newDate;
                    }
                    break;
                }
                default:
            }
        }
    }

    private checkNoValue(value: string): boolean {
        if (value == null || value == 'undefined' || value.toLowerCase() == 'no value' || value.toLowerCase() == 'null') {
            return true;
        }
        return false;
    }

    override buildTree(groupBy: string): void {
        this.treeDataSource.disconnect();
        this.treeDataSource.connect(this).pipe(
            takeUntil(this.onDispose),
            map(data => {
                this.cardItemHeights = data.map(item => {
                    switch (this.objectType) {
                        case ObjectTypes.ASSIGNMENT: {
                            switch (item.level) {
                                case 0: return 32;
                                case 1: {
                                    // if (item.isNewItems) {
                                    //     if (item.nodeLastInGroup) {
                                    //         return 144;
                                    //     }
                                    //     return 136;
                                    // }
                                    return 32;
                                }
                                default: {
                                    if (item.nodeLastInGroup) {
                                        return 144;
                                    }
                                    return 136;
                                }
                            }
                        }
                        case ObjectTypes._SEARCH: {
                            switch (item.level) {
                                case 0: return 32;
                                case 1: {
                                    if (item.object.type == ObjectTypes.TOPIC) {
                                        if (item.nodeLastInGroup) {
                                            return 144;
                                        }
                                        return 136;
                                    } else {
                                        return 32;
                                    }
                                };
                                default: return 136;
                            }
                        }
                        default: {
                            switch (item.level) {
                                case 0: return 32;
                                default: {
                                    if (item.nodeLastInGroup) {
                                        return 144;
                                    }
                                    return 136;
                                }
                            }
                        }
                    }
                });
                this.condensedItemHeights = data.map(item => {
                    switch (this.objectType) {
                        // TODO: Probably will end up implementing this at some point, but for now we dont know.
                        // case ObjectTypes.ASSIGNMENT: {
                        //     switch (item.level) {
                        //         case 0: return 40;
                        //         case 1: {
                        //             if (item.isNewItems) {
                        //                 if (item.nodeLastInGroup) {
                        //                     return 144;
                        //                 }
                        //                 return 136;
                        //             }
                        //             return 32;
                        //         }
                        //         default: return 80;
                        //     }
                        // }
                        default: {
                            switch (item.level) {
                                case 0: return 40;
                                default: {
                                    if(item.nodeLastInGroup && item.nodeFirstInGroup) {
                                        return 80;
                                    }
                                    if (item.nodeLastInGroup) {
                                        return 56;
                                    } else if (item.nodeFirstInGroup) {
                                        return 72;
                                    }
                                    return 44;
                                }
                            }
                        }
                    }
                });

                this.cardItemHeightsCustomFields = data.map(item => {
                    switch (this.objectType) {
                        case ObjectTypes.ASSIGNMENT: {
                            switch (item.level) {
                                case 0: return 32;
                                case 1: {
                                    // if (item.isNewItems) {
                                    //     if (item.nodeLastInGroup) {
                                    //         return 144;
                                    //     }
                                    //     return 136;
                                    // }
                                    return 32;
                                }
                                default: {
                                    if (item.nodeLastInGroup) {
                                        return 144;
                                    }
                                    return 136;
                                }
                            }
                        }
                        case ObjectTypes._SEARCH: {
                            switch (item.level) {
                                case 0: return 32;
                                case 1: {
                                    if (item.object.type == ObjectTypes.TOPIC) {
                                        if (item.nodeLastInGroup) {
                                            return 192;
                                        }
                                        return 184;
                                    } else {
                                        return 32;
                                    }
                                };
                                default: {
                                    if (item.nodeLastInGroup) {
                                        return 144;
                                    }
                                    return 136;
                                }
                            }
                        }
                        default: {
                            switch (item.level) {
                                case 0: return 32;
                                default: {
                                    if (item.nodeLastInGroup) {
                                        return 192;
                                    }
                                    return 184;
                                }
                            }
                        }
                    }
                });
            })
        ).subscribe();

        switch (this.objectType) {
            case ObjectTypes.TOPIC: {
                this.treeLikeData = this.topicGroups[groupBy].keys.map<TreeLikeNode<Record>>(key => {
                    return {
                        id: key.value,
                        label: key.display,
                        itemsCount: this.topicGroups[groupBy].entries[key.value].length,
                        children: this.topicGroups[groupBy].entries[key.value].map<TreeLikeNode<Record>>((entry, index) => {
                            return {
                                id: entry.guid,
                                children: [],
                                object: entry,
                                nodeFirstInGroup: index === 0,
                                nodeLastInGroup: (index === (this.topicGroups[groupBy].entries[key.value].length - 1))
                            };
                        }),
                        cost: combineLatest([...this.topicGroups[groupBy].entries[key.value].map(entry => entry.totalCostObserver)]).pipe(
                            map(costs => costs.reduce((acc, cost) => acc + cost, 0))
                        )
                    };
                });
                break;
            }
            case ObjectTypes.ASSIGNMENT: {
                this.treeLikeData = this.assignmentGroups[groupBy].keys.map<TreeLikeNode<Record>>(key => {
                    return {
                        id: key.value,
                        label: key.display,
                        itemsCount: this.assignmentGroups[groupBy].entries[key.value].length,
                        children: this.assignmentGroups[groupBy].entries[key.value].reduce((acc, entry, index) => {
                            const parent = acc.find(item => item.id === entry.parentGuid);
                            if (!parent) {
                                acc.push({
                                    id: entry.parentGuid,
                                    label: entry.parentRecord.title,
                                    object: entry,
                                    isMetadata: true,
                                    children: [{
                                        id: entry.guid,
                                        children: [],
                                        object: entry,
                                        nodeFirstInGroup: index === 0,
                                        nodeLastInGroup: (index === (this.assignmentGroups[groupBy].entries[key.value].length - 1))
                                    }]
                                })
                            } else {
                                parent.children.push({
                                    id: entry.guid,
                                    children: [],
                                    object: entry,
                                    nodeFirstInGroup: index === 0,
                                    nodeLastInGroup: (index === (this.assignmentGroups[groupBy].entries[key.value].length - 1))
                                })
                            }
                            return acc;
                        }, [] as TreeLikeNode<Record>[])//.filter((value, index, array) => array.findIndex(item => (item.id === value.id)) === index) //TODO: use reduce instead of filter (optimization)
                    };
                });
                break;
            }
            case ObjectTypes._SEARCH: {
                this.treeLikeData = [];

                if (this.topicSearchGroup && this.topicSearchGroup.entries["SingleGroup"] && this.topicSearchGroup.entries["SingleGroup"].length > 0) {
                    const topics = {
                        id: "topics",
                        label: "Topics",
                        itemsCount: this.topicSearchGroup.entries["SingleGroup"].length,
                        children: this.topicSearchGroup.entries["SingleGroup"].map<TreeLikeNode<Record>>((entry, idx) => {
                            return {
                                id: entry.guid,
                                children: [],
                                object: entry,
                                nodeFirstInGroup: idx === 0,
                                nodeLastInGroup: idx === (this.topicSearchGroup.entries["SingleGroup"].length - 1)
                            };
                        })
                    }
                    this.treeLikeData = [...this.treeLikeData, topics];
                }

                if (this.assignmentSearchGroup && this.assignmentSearchGroup.keys && this.assignmentSearchGroup.keys.length > 0) {
                    const assignments = this.assignmentSearchGroup.keys.reduce((acc, key, idx) => {


                        const childrenReducer = (acc, entry, index) => {
                            const parent = acc.find(item => item.id === entry.parentGuid);
                            if (!parent) {
                                acc.push({
                                    id: entry.parentGuid,
                                    label: entry.parentRecord.title,
                                    object: entry,
                                    isMetadata: true,
                                    children: [{
                                        id: entry.guid,
                                        children: [],
                                        object: entry,
                                        nodeFirstInGroup: index === 0,
                                        nodeLastInGroup: false
                                    }]
                                })
                            } else {
                                parent.children.push({
                                    id: entry.guid,
                                    children: [],
                                    object: entry,
                                    nodeFirstInGroup: index === 0,
                                    nodeLastInGroup: false
                                })
                            }
                            return acc;
                        }

                        if (acc.id) {
                            acc.children = [...acc.children, ...this.assignmentSearchGroup.entries[key.value].reduce(childrenReducer, [] as TreeLikeNode<Record>[])]
                        } else {
                            acc = {
                                id: "assignments",
                                label: "Assignments",
                                itemsCount: this.assignments.length,
                                children: this.assignmentSearchGroup.entries[key.value].reduce(childrenReducer, [] as TreeLikeNode<Record>[])
                            }
                        }
                        return acc;

                    }, {} as TreeLikeNode<Record>)
                    this.treeLikeData = [...this.treeLikeData, assignments];
                }
                break;
            }
        }
        // let newItems: TreeLikeNode<Record> = null;
        // if (PlaybookSearchResults.newlyAddedItems.items.length > 0) {
        //     newItems = {
        //         id: "newItems",
        //         label: "New Items",
        //         itemsCount: PlaybookSearchResults.newlyAddedItems.items.length,
        //         isNewItems: true,
        //         children: PlaybookSearchResults.newlyAddedItems.items.map<TreeLikeNode<Record>>((entry, idx) => {
        //             return {
        //                 id: entry.guid,
        //                 children: [],
        //                 object: entry,
        //                 nodeFirstInGroup: idx === 0,
        //                 nodeLastInGroup: idx === (PlaybookSearchResults.newlyAddedItems.items.length - 1),
        //                 isNewItems: true
        //             };
        //         }),
        //         cost: combineLatest([...PlaybookSearchResults.newlyAddedItems.items.map(entry => entry.totalCostObserver)]).pipe(
        //             map(costs => costs.reduce((acc, cost) => acc + cost, 0))
        //         )
        //     }
        // }
        // if (newItems) {
        //     this.treeDataSource.data = [newItems, ...this.treeLikeData];
        // } else {
            this.treeDataSource.data = this.treeLikeData;
        // }

        this.treeControl.expandAll();
    }

}

DataStore.registerSearchResults("playbook", new PlaybookSearchResults());
